/* eslint-disable camelcase */
import React from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    NO_DANGEROUS_FAILURE,
    NO_SAFE_FAILURE,
    NO_ARCHITECTURAL_CONSTRAINTS,
    IEC_61508_2010,
    IEC_61508_1998,
    IEC_61511_2016,
    IEC_61511_2003,
    ISA_TR84_2002,
    ISA_TR84_2022,
    VDI_VDE_2180_2019,

    ARCHITECTURE_1oo1,
    ARCHITECTURE_1oo2,
    ARCHITECTURE_1oo2D,
    ARCHITECTURE_1oo3,
    ARCHITECTURE_2oo2,
    ARCHITECTURE_2oo3,
    ARCHITECTURE_3oo3,

    YES, NO,
} from "const"
import { SilSubsystem } from "server/models"
import {
    StoreType,
    setSilSafetyFunctionSubsystemList,
} from "client/store"
import {
    deepCopy,
    KeyValue,
    SilCalculationMethod,
    SilArchitecturalConstraints,
} from "client/utils"
import {
    FormGroup,
    Label,
    LabelValue,
    SelectDropdown,
    SelectDropdownItem,
    Input,
    RadioButton,
} from "client/components"

import {
    ModelNestedWrapper,
    SettingsWrapper,
    SettingsTitle,
} from "client/pages/sil/modelStyled"

import FormatPercentage from "client/pages/sil/format/percentage"
import { customParseInt } from "client/pages/sil/format/parse"


interface SettingsProps {
    calculationMethodDangerous: SilCalculationMethod
    calculationMethodSafe: SilCalculationMethod
}

const Settings = ({
    calculationMethodDangerous,
    calculationMethodSafe,
}: SettingsProps) => {
    const subsystemList = useSelector((state: StoreType) => state.silSafetyFunction.subsystemList)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    // calculation method can be or dangerous or safe or "no dangerous/safe failure", never both
    let calculationMethod: SilCalculationMethod = null
    if (calculationMethodDangerous !== NO_DANGEROUS_FAILURE) calculationMethod = calculationMethodDangerous
    if (calculationMethodSafe !== NO_SAFE_FAILURE) calculationMethod = calculationMethodSafe

    const changeSetting = (
        setting: KeyValue<SilSubsystem>,
        index: number,
    ) => {
        const newArr: SilSubsystem[] = deepCopy(subsystemList)
        newArr[index] = {
            ...newArr[index],
            ...setting,
        }
        dispatch(setSilSafetyFunctionSubsystemList(newArr))
    }

    const changeArchitecturalConstraints = (
        constraints: SilArchitecturalConstraints,
        index: number,
    ) => {
        const newArr: SilSubsystem[] = deepCopy(subsystemList)
        newArr[index] = {
            ...newArr[index],
            architecturalConstraints: constraints,
        }
        dispatch(setSilSafetyFunctionSubsystemList(newArr))
    }

    return (
        <ModelNestedWrapper>
            {
                subsystemList.map((subsystem, index) => {
                    const { architecture } = subsystem
                    const constraints = subsystem.architecturalConstraints
                    let constraintsTitle = ""
                    if (constraints === NO_ARCHITECTURAL_CONSTRAINTS) constraintsTitle = lang.noArchitecturalConstraints
                    if (constraints === IEC_61508_2010) constraintsTitle = lang.iec_61508_2010
                    if (constraints === IEC_61508_1998) constraintsTitle = lang.iec_61508_1998
                    if (constraints === IEC_61511_2016) constraintsTitle = lang.iec_61511_2016
                    if (constraints === IEC_61511_2003) constraintsTitle = lang.iec_61511_2003

                    let isatr842022tiShow = true
                    let isatr842022diShow = true
                    let isatr842022mttrShow = true
                    let isatr842022betaShow = true

                    if (calculationMethod === ISA_TR84_2022) {
                        // calculation method can be or dangerous or safe or "no dangerous/safe failure", never both
                        if (calculationMethodSafe === ISA_TR84_2022) {
                            if (
                                subsystem.architecture === ARCHITECTURE_1oo1
                                || subsystem.architecture === ARCHITECTURE_1oo2
                                || subsystem.architecture === ARCHITECTURE_1oo3
                            ) {
                                isatr842022tiShow = false
                                isatr842022mttrShow = false
                            }
                            isatr842022diShow = false
                            isatr842022betaShow = false
                        }

                        if (calculationMethodDangerous === ISA_TR84_2022) {
                            if (
                                subsystem.architecture === ARCHITECTURE_1oo1
                                || subsystem.architecture === ARCHITECTURE_2oo2
                                || subsystem.architecture === ARCHITECTURE_3oo3
                            ) {
                                isatr842022betaShow = false
                            }
                        }
                    }

                    return (
                        // eslint-disable-next-line react/no-array-index-key
                        <SettingsWrapper key={index}>
                            <SettingsTitle>{subsystem.name}</SettingsTitle>
                            {
                                calculationMethod === IEC_61508_2010 && (
                                    <div>
                                        <FormGroup>
                                            <Label>
                                                {lang.t1}
                                                <LabelValue>{lang.hoursLabel}</LabelValue>
                                            </Label>
                                            <Input
                                                type="text"
                                                value={subsystem.iec615082010t1 || ""}
                                                onChange={(e) => changeSetting({ iec615082010t1: e.target.value }, index)}
                                                onBlur={(e) => changeSetting({ iec615082010t1: customParseInt(e.target.value) }, index)}
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <Label>
                                                {lang.mttr}
                                                <LabelValue>{lang.hoursLabel}</LabelValue>
                                            </Label>
                                            <Input
                                                type="text"
                                                value={subsystem.iec615082010mttr || ""}
                                                onChange={(e) => changeSetting({ iec615082010mttr: e.target.value }, index)}
                                                onBlur={(e) => changeSetting({ iec615082010mttr: customParseInt(e.target.value) }, index)}
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <Label>
                                                {lang.mrt}
                                                <LabelValue>{lang.hoursLabel}</LabelValue>
                                            </Label>
                                            <Input
                                                type="text"
                                                value={subsystem.iec615082010mrt || ""}
                                                onChange={(e) => changeSetting({ iec615082010mrt: e.target.value }, index)}
                                                onBlur={(e) => changeSetting({ iec615082010mrt: customParseInt(e.target.value) }, index)}
                                            />
                                        </FormGroup>

                                        {
                                            (
                                                architecture === ARCHITECTURE_1oo2
                                                || architecture === ARCHITECTURE_1oo2D
                                                || architecture === ARCHITECTURE_1oo3
                                                || architecture === ARCHITECTURE_2oo3
                                            ) && (
                                                <FormGroup>
                                                    <FormatPercentage
                                                        title={lang.beta}
                                                        data={subsystem.iec615082010beta}
                                                        onChange={(value) => changeSetting({ iec615082010beta: value }, index)}
                                                    />
                                                </FormGroup>
                                            )
                                        }
                                        {
                                            (
                                                architecture === ARCHITECTURE_1oo2
                                                || architecture === ARCHITECTURE_1oo2D
                                                || architecture === ARCHITECTURE_1oo3
                                                || architecture === ARCHITECTURE_2oo3
                                            ) && (
                                                <FormatPercentage
                                                    title={lang.betaD}
                                                    data={subsystem.iec615082010betaD}
                                                    onChange={(value) => changeSetting({ iec615082010betaD: value }, index)}
                                                />
                                            )
                                        }
                                        {
                                            architecture === ARCHITECTURE_1oo2D && (
                                                <FormatPercentage
                                                    title={lang.k}
                                                    data={subsystem.iec615082010k}
                                                    onChange={(value) => changeSetting({ iec615082010k: value }, index)}
                                                />
                                            )
                                        }
                                    </div>
                                )
                            }
                            {
                                calculationMethod === IEC_61508_1998 && (
                                    <div>
                                        <FormGroup>
                                            <Label>
                                                {lang.t1}
                                                <LabelValue>{lang.hoursLabel}</LabelValue>
                                            </Label>
                                            <Input
                                                type="text"
                                                value={subsystem.iec615081998t1 || ""}
                                                onChange={(e) => changeSetting({ iec615081998t1: e.target.value }, index)}
                                                onBlur={(e) => changeSetting({ iec615081998t1: customParseInt(e.target.value) }, index)}
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <Label>
                                                {lang.mttr}
                                                <LabelValue>{lang.hoursLabel}</LabelValue>
                                            </Label>
                                            <Input
                                                type="text"
                                                value={subsystem.iec615081998mttr || ""}
                                                onChange={(e) => changeSetting({ iec615081998mttr: e.target.value }, index)}
                                                onBlur={(e) => changeSetting({ iec615081998mttr: customParseInt(e.target.value) }, index)}
                                            />
                                        </FormGroup>

                                        {
                                            (
                                                architecture === ARCHITECTURE_1oo2
                                                || architecture === ARCHITECTURE_1oo2D
                                                || architecture === ARCHITECTURE_2oo3
                                            ) && (
                                                <FormatPercentage
                                                    title={lang.beta}
                                                    data={subsystem.iec615081998beta}
                                                    onChange={(value) => changeSetting({ iec615081998beta: value }, index)}
                                                />
                                            )
                                        }
                                    </div>
                                )
                            }
                            {
                                calculationMethod === ISA_TR84_2002 && (
                                    <div>
                                        <FormGroup>
                                            <Label>
                                                {lang.t1}
                                                <LabelValue>{lang.hoursLabel}</LabelValue>
                                            </Label>
                                            <Input
                                                type="text"
                                                value={subsystem.tr842002t1 || ""}
                                                onChange={(e) => changeSetting({ tr842002t1: e.target.value }, index)}
                                                onBlur={(e) => changeSetting({ tr842002t1: customParseInt(e.target.value) }, index)}
                                            />
                                        </FormGroup>
                                        {
                                            architecture !== ARCHITECTURE_1oo1 && (
                                                <FormGroup>
                                                    <Label>
                                                        {lang.mttr}
                                                        <LabelValue>{lang.hoursLabel}</LabelValue>
                                                    </Label>
                                                    <Input
                                                        type="text"
                                                        value={subsystem.tr842002mttr || ""}
                                                        onChange={(e) => changeSetting({ tr842002mttr: e.target.value }, index)}
                                                        onBlur={(e) => changeSetting({
                                                            tr842002mttr: customParseInt(e.target.value),
                                                        }, index)}
                                                    />
                                                </FormGroup>
                                            )
                                        }
                                        {
                                            architecture !== ARCHITECTURE_1oo1 && (
                                                <FormatPercentage
                                                    title={lang.beta}
                                                    data={subsystem.tr842002beta}
                                                    onChange={(value) => changeSetting({ tr842002beta: value }, index)}
                                                />
                                            )
                                        }
                                        <FormatPercentage
                                            title={lang.safeSystematicFailure}
                                            data={subsystem.tr842002safeFailure}
                                            onChange={(value) => changeSetting({ tr842002safeFailure: value }, index)}
                                        />
                                        <FormatPercentage
                                            title={lang.dangerousSystematicFailure}
                                            data={subsystem.tr842002dangerousFailure}
                                            onChange={(value) => changeSetting({ tr842002dangerousFailure: value }, index)}
                                        />
                                    </div>
                                )
                            }
                            {
                                calculationMethod === ISA_TR84_2022 && (
                                    <div>
                                        {
                                            isatr842022tiShow && (
                                                <FormGroup>
                                                    <Label>
                                                        {lang.ti}
                                                        <LabelValue>{lang.hoursLabel}</LabelValue>
                                                    </Label>
                                                    <Input
                                                        type="text"
                                                        value={subsystem.isatr842022ti || ""}
                                                        onChange={(e) => changeSetting({ isatr842022ti: e.target.value }, index)}
                                                        onBlur={(e) => changeSetting({
                                                            isatr842022ti: customParseInt(e.target.value),
                                                        }, index)}
                                                    />
                                                </FormGroup>
                                            )
                                        }
                                        {
                                            isatr842022diShow && (
                                                <FormGroup>
                                                    <Label>
                                                        {lang.di}
                                                        <LabelValue>{lang.hoursLabel}</LabelValue>
                                                    </Label>
                                                    <Input
                                                        type="text"
                                                        value={subsystem.isatr842022di || ""}
                                                        onChange={(e) => changeSetting({ isatr842022di: e.target.value }, index)}
                                                        onBlur={(e) => changeSetting({
                                                            isatr842022di: customParseInt(e.target.value),
                                                        }, index)}
                                                    />
                                                </FormGroup>
                                            )
                                        }
                                        {
                                            isatr842022mttrShow && (
                                                <FormGroup>
                                                    <Label>
                                                        {lang.mttr}
                                                        <LabelValue>{lang.hoursLabel}</LabelValue>
                                                    </Label>
                                                    <Input
                                                        type="text"
                                                        value={subsystem.isatr842022mttr || ""}
                                                        onChange={(e) => changeSetting({ isatr842022mttr: e.target.value }, index)}
                                                        onBlur={(e) => changeSetting({
                                                            isatr842022mttr: customParseInt(e.target.value),
                                                        }, index)}
                                                    />
                                                </FormGroup>
                                            )
                                        }
                                        {
                                            isatr842022betaShow && (
                                                <FormatPercentage
                                                    title={lang.beta}
                                                    data={subsystem.isatr842022beta || ""}
                                                    onChange={(value) => changeSetting({ isatr842022beta: value }, index)}
                                                />
                                            )
                                        }
                                        <FormGroup>
                                            <Label>
                                                {calculationMethodSafe === ISA_TR84_2022 && lang.detectedFailuresLeadToSafeState}
                                                {calculationMethodDangerous === ISA_TR84_2022 && lang.detectedFailuresLeadToDangerousState}
                                            </Label>
                                            <FormGroup inline>
                                                <RadioButton
                                                    checked={subsystem.isatr842022safeState === YES}
                                                    title={lang.yes}
                                                    onChange={() => changeSetting({ isatr842022safeState: YES }, index)}
                                                />
                                                <RadioButton
                                                    checked={subsystem.isatr842022safeState === NO}
                                                    title={lang.no}
                                                    onChange={() => changeSetting({ isatr842022safeState: NO }, index)}
                                                />
                                            </FormGroup>
                                        </FormGroup>
                                    </div>
                                )
                            }
                            {
                                calculationMethod === VDI_VDE_2180_2019 && (
                                    <div>
                                        <FormatPercentage
                                            title={lang.ptc0A}
                                            data={subsystem.vdivde21802019ptc0A}
                                            onChange={(value) => changeSetting({ vdivde21802019ptc0A: value }, index)}
                                        />
                                        <FormatPercentage
                                            title={lang.ptc1A}
                                            data={subsystem.vdivde21802019ptc1A}
                                            onChange={(value) => changeSetting({ vdivde21802019ptc1A: value }, index)}
                                        />
                                        {
                                            (
                                                architecture === ARCHITECTURE_1oo2
                                                || architecture === ARCHITECTURE_1oo3
                                                || architecture === ARCHITECTURE_2oo2
                                                || architecture === ARCHITECTURE_2oo3
                                            ) && (
                                                <div>
                                                    <FormatPercentage
                                                        title={lang.ptc0B}
                                                        data={subsystem.vdivde21802019ptc0B}
                                                        onChange={(value) => changeSetting({ vdivde21802019ptc0B: value }, index)}
                                                    />
                                                    <FormatPercentage
                                                        title={lang.ptc1B}
                                                        data={subsystem.vdivde21802019ptc1B}
                                                        onChange={(value) => changeSetting({ vdivde21802019ptc1B: value }, index)}
                                                    />
                                                </div>
                                            )
                                        }
                                        {
                                            (
                                                architecture === ARCHITECTURE_1oo3
                                                || architecture === ARCHITECTURE_2oo3
                                            ) && (
                                                <div>
                                                    <FormatPercentage
                                                        title={lang.ptc0C}
                                                        data={subsystem.vdivde21802019ptc0C}
                                                        onChange={(value) => changeSetting({ vdivde21802019ptc0C: value }, index)}
                                                    />
                                                    <FormatPercentage
                                                        title={lang.ptc1C}
                                                        data={subsystem.vdivde21802019ptc1C}
                                                        onChange={(value) => changeSetting({ vdivde21802019ptc1C: value }, index)}
                                                    />
                                                </div>
                                            )
                                        }
                                        {
                                            (
                                                architecture === ARCHITECTURE_1oo2
                                                || architecture === ARCHITECTURE_1oo3
                                                || architecture === ARCHITECTURE_2oo3
                                            ) && (
                                                <FormatPercentage
                                                    title={lang.beta}
                                                    data={subsystem.vdivde21802019beta}
                                                    onChange={(value) => changeSetting({ vdivde21802019beta: value }, index)}
                                                />
                                            )
                                        }
                                        <FormGroup>
                                            <Label>
                                                {lang.t0}
                                                <LabelValue>{lang.hoursLabel}</LabelValue>
                                            </Label>
                                            <Input
                                                type="text"
                                                value={subsystem.vdivde21802019t0 || ""}
                                                onChange={(e) => changeSetting({ vdivde21802019t0: e.target.value }, index)}
                                                onBlur={(e) => changeSetting({ vdivde21802019t0: customParseInt(e.target.value) }, index)}
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <Label>
                                                {lang.t1}
                                                <LabelValue>{lang.hoursLabel}</LabelValue>
                                            </Label>
                                            <Input
                                                type="text"
                                                value={subsystem.vdivde21802019t1 || ""}
                                                onChange={(e) => changeSetting({ vdivde21802019t1: e.target.value }, index)}
                                                onBlur={(e) => changeSetting({ vdivde21802019t1: customParseInt(e.target.value) }, index)}
                                            />
                                        </FormGroup>
                                        <FormGroup>
                                            <Label>
                                                {lang.t2}
                                                <LabelValue>{lang.hoursLabel}</LabelValue>
                                            </Label>
                                            <Input
                                                type="text"
                                                value={subsystem.vdivde21802019t2 || ""}
                                                onChange={(e) => changeSetting({ vdivde21802019t2: e.target.value }, index)}
                                                onBlur={(e) => changeSetting({ vdivde21802019t2: customParseInt(e.target.value) }, index)}
                                            />
                                        </FormGroup>
                                    </div>
                                )
                            }

                            {
                                calculationMethodDangerous !== NO_DANGEROUS_FAILURE && (
                                    // display only for dangerous method
                                    <FormGroup>
                                        <Label>{lang.architecturalConstraints}</Label>
                                        <SelectDropdown title={constraintsTitle}>
                                            <SelectDropdownItem
                                                onClick={() => changeArchitecturalConstraints(NO_ARCHITECTURAL_CONSTRAINTS, index)}
                                            >
                                                {lang.noArchitecturalConstraints}
                                            </SelectDropdownItem>
                                            <SelectDropdownItem onClick={() => changeArchitecturalConstraints(IEC_61508_2010, index)}>
                                                {lang.iec_61508_2010}
                                            </SelectDropdownItem>
                                            <SelectDropdownItem onClick={() => changeArchitecturalConstraints(IEC_61508_1998, index)}>
                                                {lang.iec_61508_1998}
                                            </SelectDropdownItem>
                                            <SelectDropdownItem onClick={() => changeArchitecturalConstraints(IEC_61511_2016, index)}>
                                                {lang.iec_61511_2016}
                                            </SelectDropdownItem>
                                            <SelectDropdownItem onClick={() => changeArchitecturalConstraints(IEC_61511_2003, index)}>
                                                {lang.iec_61511_2003}
                                            </SelectDropdownItem>
                                        </SelectDropdown>
                                    </FormGroup>
                                )
                            }
                        </SettingsWrapper>
                    )
                })
            }
        </ModelNestedWrapper>
    )
}
export default Settings
